import { Close, Delete, Download } from "icons";
import { Icon } from "minimum-ui/core";
import { color } from "minimum-ui/tokens";
import styled from "styled-components";
import { H4M, H4R } from "typography/headers";

import { Button, Spinner } from "./index";

export interface FileInfoProps {
  loading?: boolean;
  onCancel?: () => void;
  onDelete?: null | (() => void);
  name: string;
  description?: string;
  onClick?: () => void;
  url?: string;
  showDownload?: boolean;
  disabled?: boolean;
  showPreview?: boolean;
  onPreview?: string | undefined;
  showReplace?: boolean;
  onReplace?: null | (() => void);
}

const FileInfo: React.FC<FileInfoProps> = ({
  loading,
  name,
  description,
  url,
  onCancel,
  onDelete,
  onClick,
  showDownload,
  disabled,
  children,
  showPreview,
  onPreview,
  showReplace,
  onReplace,
}) => {
  const handleCancel = () => {
    if (loading && onCancel) {
      onCancel();
    }
  };

  const handleDelete = () => {
    if (!loading && onDelete) {
      onDelete();
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (url) {
      window.open(url);
    }
  };

  const handleReplace = () => {
    if (!loading && onReplace) {
      onReplace();
    }
  };

  return (
    <Wrapper>
      {loading && <Spinner />}
      <FileDetails>
        {showPreview && (
          <Button variant="text-primary" newTab={true} onClick={onPreview}>
            <Icon
              icon="previewNewTab"
              color={color.foreground.primaryAction}
              size="sm"
            />
          </Button>
        )}
        <FileName> {name}</FileName>
        {description && <FileDescription> {description}</FileDescription>}
      </FileDetails>
      {loading && onCancel ? (
        <ActionIcon as={Close} size={20} onClick={handleCancel} />
      ) : (
        children || (
          <>
            {showReplace && !disabled && (
              <Icon
                icon="replace"
                color={color.foreground.primaryAction}
                size="sm"
                onClick={handleReplace}
              />
            )}
            {onDelete && !disabled && (
              <ActionIcon
                data-testid="delete-file"
                as={Delete}
                size={20}
                onClick={handleDelete}
              />
            )}
            {showDownload && !disabled && (
              <ActionIcon
                data-testid="download-file"
                as={Download}
                size={20}
                onClick={handleClick}
              />
            )}
          </>
        )
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  gap: 16px;
  background-color: ${color.neutral[100]};
  border: 1px solid ${color.border.default};
  border-radius: 12px;
`;

const FileDetails = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  margin-top: -4px;
  width: fit-content;
  overflow: hidden;
  word-wrap: break-word;
`;

const FileName = styled(H4M)`
  margin: 0;
  width: 100%;
`;

const FileDescription = styled(H4R)`
  color: ${color.neutral[400]};
`;

const ActionIcon = styled.div`
  color: ${color.blue[300]};
  cursor: pointer;
`;

export default FileInfo;
